import React, { useRef } from 'react';
import type { TabId } from './types';

export interface TabDefinition {
  id: TabId;
  label: string;
}

interface StatsTabsProps {
  tabs: TabDefinition[];
  selected: TabId;
  onSelect: (id: TabId) => void;
  /** Ids so each tab and its panel can point at each other. */
  tabElementId: (id: TabId) => string;
  panelElementId: (id: TabId) => string;
}

/**
 * A plain WAI-ARIA tablist.
 *
 * Not jQuery UI tabs, which the player uses: driving a global from React makes
 * the component untestable in isolation and gives no keyboard behavior we can
 * assert. The styling reuses the app's own `--tab-*` variables, which are
 * semi-transparent by design and therefore already correct in both themes.
 */
export default function StatsTabs({
  tabs,
  selected,
  onSelect,
  tabElementId,
  panelElementId,
}: StatsTabsProps) {
  const buttonRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});

  function moveTo(index: number) {
    const next = tabs[(index + tabs.length) % tabs.length];

    onSelect(next.id);
    // Automatic activation - selection follows focus, which WAI-ARIA
    // recommends when panels are cheap to show. These render from data that has
    // already been fetched, so there is nothing to defer behind an Enter press.
    buttonRefs.current[next.id]?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case 'ArrowLeft':
        moveTo(index - 1);
        break;
      case 'ArrowRight':
        moveTo(index + 1);
        break;
      case 'Home':
        moveTo(0);
        break;
      case 'End':
        moveTo(tabs.length - 1);
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  return (
    <div className="system-stats__tablist" role="tablist" aria-label="System statistics">
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          ref={(element) => {
            buttonRefs.current[tab.id] = element;
          }}
          type="button"
          role="tab"
          id={tabElementId(tab.id)}
          aria-selected={tab.id === selected}
          aria-controls={panelElementId(tab.id)}
          // Roving tabindex: the whole strip is one Tab stop and the arrow keys
          // move within it.
          tabIndex={tab.id === selected ? 0 : -1}
          className={`system-stats__tab${tab.id === selected ? ' system-stats__tab--selected' : ''}`}
          onClick={() => onSelect(tab.id)}
          onKeyDown={(event) => handleKeyDown(event, index)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
